import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import { WithLoader } from 'Components/WithLoader'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import { useGetSingleCatalogQuery } from 'Services/ProductManagement/Features/Catalog/catalogApi'
import TariffCatalogAllowedApiClients from 'Services/ProductManagement/Features/Tariff/Components/TariffCatalogApiClients'
import { TariffCatalogDetails } from 'Services/ProductManagement/Features/Tariff/Components/TariffCatalogDetails'
import TariffCatalogProductsEdit from 'Services/ProductManagement/Features/Tariff/Components/TariffCatalogProductsEdit'
import {
  type Tariff,
  TariffCatalogTabType,
  TariffStatus
} from 'Services/ProductManagement/Features/Tariff/types'
import { useTypedSelector } from 'Store'
import {
  DRAWER_TAB_PARAM_KEY,
  IS_TARIFF_EDIT_ALLOWED,
  PRODUCT_MANAGEMENT_ROOT_URL
} from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

interface TariffCatalogDrawerProps {
  open: boolean
  handleClose: () => void
  tariff: Tariff
  selectedCatalogId: string | undefined
  handleCatalogDelete: () => void
}

export function TariffCatalogDrawer({
  open,
  handleClose,
  tariff,
  selectedCatalogId,
  handleCatalogDelete
}: Readonly<TariffCatalogDrawerProps>) {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const navigate = useNavigate()
  const { id: tariffId } = useParams()
  const { user } = useTypedSelector(state => state.user)
  const [searchParams, setSearchParams] = useSearchParams()
  const defaultDrawerTab = TariffCatalogTabType.PRODUCTS
  const drawerTabFromParams = searchParams.get(DRAWER_TAB_PARAM_KEY) ?? defaultDrawerTab

  const { confirm } = useConfirm()

  const { data: singleCatalog, isFetching: isSingleCatalogFetching } = useGetSingleCatalogQuery(
    selectedCatalogId ?? '',
    {
      skip: !selectedCatalogId
    }
  )

  const handleDrawerTabChange = (_event: React.SyntheticEvent, newValue: TariffCatalogTabType) => {
    searchParams.set(DRAWER_TAB_PARAM_KEY, newValue)
    setSearchParams(searchParams)
  }

  /**
   * Superuser can delete tariff's catalog in any tariff status.
   * Authority admin can only delete tariff's catalog when tariff is in DRAFT or STAGED status.
   */
  const isCatalogDeleteAllowed =
    isSuperuser(user) || [TariffStatus.DRAFT, TariffStatus.STAGED].includes(tariff.status)

  return (
    <DrawerRight
      open={open}
      onClose={handleClose}
      topBar={
        <Box>
          {IS_TARIFF_EDIT_ALLOWED && singleCatalog && (
            <>
              {isCatalogDeleteAllowed && (
                <DeleteButton
                  sx={{ mr: 1 }}
                  size="small"
                  onClick={() =>
                    confirm({
                      title: t`Are you sure?`,
                      message: t`Are you sure you want to delete the catalog?`,
                      handleConfirm: () => {
                        handleCatalogDelete()
                      }
                    })
                  }
                >{t`Delete`}</DeleteButton>
              )}
              <CustomButton
                onClick={() =>
                  navigate(`${baseUrl}/${tariffId}/sales-channels/${selectedCatalogId}/edit`)
                }
                size="small"
                startIcon={<EditIcon />}
              >{t`Edit`}</CustomButton>
            </>
          )}
        </Box>
      }
    >
      <TabContext value={drawerTabFromParams}>
        <TabList onChange={handleDrawerTabChange} aria-label={t`Tariff sales channel tab list`}>
          <Tab label={t`Sales channel products`} value={TariffCatalogTabType.PRODUCTS} />
          <Tab label={t`Sales channel details`} value={TariffCatalogTabType.DETAILS} />
          {isSuperuser(user) && (
            <Tab label={t`Allowed API clients`} value={TariffCatalogTabType.ALLOWED_API_CLIENTS} />
          )}
        </TabList>
        <Divider />
        <TabPanel sx={{ p: 0 }} value={TariffCatalogTabType.PRODUCTS}>
          <WithLoader loading={isSingleCatalogFetching}>
            {singleCatalog && (
              <TariffCatalogProductsEdit
                tariff={tariff}
                catalog={singleCatalog}
                handleCancel={handleClose}
              />
            )}
          </WithLoader>
        </TabPanel>
        <TabPanel sx={{ p: 0 }} value={TariffCatalogTabType.DETAILS}>
          {singleCatalog && (
            <TariffCatalogDetails
              catalog={singleCatalog}
              tariff={tariff}
              singleLoading={isSingleCatalogFetching}
            />
          )}
        </TabPanel>
        {isSuperuser(user) && (
          <TabPanel sx={{ p: 0 }} value={TariffCatalogTabType.ALLOWED_API_CLIENTS}>
            <WithLoader loading={isSingleCatalogFetching}>
              {singleCatalog && (
                <TariffCatalogAllowedApiClients catalog={singleCatalog} handleClose={handleClose} />
              )}
            </WithLoader>
          </TabPanel>
        )}
      </TabContext>
    </DrawerRight>
  )
}
